import React, { FC } from "react";

import { formatDateTime } from "~/libs";

import { Avatar } from "~/ui";

export type ProgressTableColumn = {
  date: string;
  isWeekend?: boolean;
};

export type ProgressTableEmployee = {
  id: string;
  name: string;
  avatarUrl: string | null;
};

export type ProgressTableCellProps = {
  date: string;
  employee: ProgressTableEmployee;
  isWeekend: boolean;
};

type ProgressTableBaseProps = {
  columns: ProgressTableColumn[];
  employees: ProgressTableEmployee[];
  renderCell: (props: ProgressTableCellProps) => React.ReactNode;
  tableHeader?: React.ReactNode;
  className?: string;
};

export const ProgressTableBase: FC<ProgressTableBaseProps> = ({
  columns,
  employees,
  renderCell,
  tableHeader,
  className = "",
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      {/* 任意のヘッダー */}
      {tableHeader}

      {/* テーブル */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead>
            <tr className="bg-gradient-to-r from-secondary-100 to-secondary-200">
              <th />
              {columns.map(({ date, isWeekend }) => {
                const weekendClass = isWeekend
                  ? "bg-red-50 text-red-700"
                  : "text-secondary-600";

                return (
                  <th
                    key={date}
                    className={`px-4 py-2 text-center font-semibold border-r border-secondary-300 min-w-[80px] ${weekendClass}`}
                  >
                    <div className="text-xs font-medium mb-1 opacity-70">
                      {formatDateTime(date, "EEE")}
                    </div>
                    <div className="text-sm font-bold">
                      {formatDateTime(date, "MM/dd")}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white">
            {employees.map((employee, employeeIndex) => (
              <tr
                key={employee.id}
                className={`hover:bg-secondary-100 transition-colors duration-150 ${
                  employeeIndex % 2 === 0 ? "bg-white" : "bg-secondary-100/30"
                }`}
              >
                <td className="sticky left-0 z-layout bg-white shadow-sm px-6 py-2 text-secondary-900 border-r border-secondary-300">
                  <div className="flex items-center">
                    <Avatar src={employee.avatarUrl || ""} size="xs" />
                    <span className="text-sm font-medium text-gray-900 ml-3">
                      {employee.name}
                    </span>
                  </div>
                </td>
                {columns.map(({ date, isWeekend }) => {
                  const weekendBgClass = isWeekend ? "bg-red-25" : "";

                  return (
                    <td
                      key={date}
                      className={`px-4 py-2 text-center border-r border-secondary-300 ${weekendBgClass}`}
                    >
                      <div className="flex items-center justify-center min-h-[40px]">
                        {renderCell({
                          date,
                          employee,
                          isWeekend: !!isWeekend,
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
